import type { BootstrapConfig, CaptureResult, PostHog } from 'posthog-js';

type NativeContext = Required<Pick<BootstrapConfig, 'distinctID' | 'isIdentifiedID' | 'sessionID'>>;
type Bridge = { postMessage(message: string): void; injectedObjectJson?: () => string };

function bridge(): Bridge | undefined {
  return (window as Window & { ReactNativeWebView?: Bridge }).ReactNativeWebView;
}

function hasSessionBridge() {
  try { return JSON.parse(bridge()?.injectedObjectJson?.() || '{}').posthogSessionBridge === 1; }
  catch { return false; }
}

function isContext(value: unknown): value is NativeContext {
  if (!value || typeof value !== 'object') return false;
  const context = value as NativeContext;
  return typeof context.distinctID === 'string' && !!context.distinctID &&
    typeof context.isIdentifiedID === 'boolean' && typeof context.sessionID === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(context.sessionID);
}

/** Bootstrap before the first pageview; subsequent native rollovers also reset replay. */
export function connectNativePostHog(
  client: PostHog,
  initialize: (options: { bootstrap?: BootstrapConfig; before_send?: (event: CaptureResult | null) => CaptureResult | null }) => void,
) {
  if (!hasSessionBridge()) { initialize({}); return () => {}; }
  let context: NativeContext | null = null;
  let initialized = false;
  let lastActivity = 0;
  const receive = (event: Event) => {
    const next = (event as CustomEvent).detail;
    if (next !== null && !isContext(next)) return;
    if (!initialized) {
      context = next;
      initialized = true;
      initialize(next ? {
        bootstrap: next,
        // Never let the embedded browser's independent idle timer create a second
        // session. Native activity/foreground messages supply the next session.
        before_send: capture => {
          if (capture && context) capture.properties.$session_id = context.sessionID;
          return capture;
        },
      } : {});
    } else if (next && (next.sessionID !== context?.sessionID || next.distinctID !== context?.distinctID || next.isIdentifiedID !== context?.isIdentifiedID)) {
      context = next;
      client.reset({ bootstrap: next });
      client.get_session_id();
    }
  };
  const activity = () => {
    const now = Date.now();
    if (now - lastActivity < 1000) return;
    lastActivity = now;
    bridge()?.postMessage(JSON.stringify({ type: 'posthog-context-request' }));
  };
  window.addEventListener('drgradus-posthog-context', receive);
  for (const name of ['pointerdown', 'keydown', 'scroll']) window.addEventListener(name, activity, { capture: true, passive: true });
  bridge()?.postMessage(JSON.stringify({ type: 'posthog-context-request', initial: true }));
  return () => {
    window.removeEventListener('drgradus-posthog-context', receive);
    for (const name of ['pointerdown', 'keydown', 'scroll']) window.removeEventListener(name, activity, true);
  };
}
